/**
 * @deprecated This file re-exports from feature modules for backwards compatibility.
 *
 * Migration paths:
 * - Bank accounts: import from "../../features/bank-accounts"
 * - Transaction charge settings: import from "../../features/transaction-charge-settings"
 * - Withdrawals: import from "../../features/withdrawals"
 * - Payouts (Balance/History): import from "../../features/payouts"
 */

import {useEffect} from "react";
import {usePayoutSettingsContext} from "../components/payouts/v2/tabs/payout-settings-tab/context";
import {ChargeBearer} from "../components/payouts/v2/tabs/transaction-settings-tab";
import {
  useGetPayoutCountries as useDomainGetPayoutCountries,
  useGetPayoutBanksQuery as useDomainGetPayoutBanksQuery,
  useGetPayoutBankBranchesQuery as useDomainGetPayoutBankBranchesQuery,
  useGetPayoutBanks as useDomainGetPayoutBanks,
  useGetBankBranches as useDomainGetBankBranches,
  useValidateBankAccount as useDomainValidateBankAccount,
} from "../features/bank-accounts";
import {
  useFetchTransactionChargeSettings as useFeatureChargeSettings,
  useUpdateTransactionChargeSettings as useFeatureUpdateChargeSettings,
} from "../features/transaction-charge-settings";
import {
  useFetchAccountBalance as useFeatureFetchAccountBalance,
  useFetchPayoutHistory as useFeatureFetchPayoutHistory,
  useWithdrawalDetails as useFeatureWithdrawalDetails,
} from "../features/payouts";

// Re-export bank account hooks that don't have context side effects
// Hooks with context dispatch (useGetPayoutCountries, useGetPayoutBanksQuery,
// useGetPayoutBankBranchesQuery, useValidateBankAccount) are kept below for now.
// useGetPayoutBanks and useGetBankBranches are kept here for backwards compatibility
// (old code relies on untyped axios response shape).
export {
  useGetConnectedBankAccount,
  useVerifyAccountNumberMutation,
  useConnectBank,
  useUpdateConnectedBank,
  useCompleteStripeRegistration,
  useLoginToStripe,
  // Stripe integration - migrated to bank-accounts feature
  useConnectToStripe,
  // New v2 Stripe hooks
  useOnboardStripe,
  useInitiateStripeUpdate,
  useCompleteStripeUpdate,
  // New v2 disconnect hook
  useDisconnectBankAccount,
} from "../features/bank-accounts";
// Re-export Stripe payload types for backwards compatibility
export type {TConnectToStripePayload} from "../features/bank-accounts";

export type TPayoutCountriesApiResponse = {
  countries: {
    name: string;
    code: string;
    currency: string;
  }[];
};

export type TGetPayoutBanksPayload = {
  countryCode: string | null;
};
export type TGetPayoutBankBranchesPayload = {
  bankId: number | null;
};
export type TGetPayoutBankBranchesApiResponse = {
  branches: {
    data: {
      id: number;
      branch_code: string;
      branch_name: string;
      swift_code: null;
      bic: null;
      bank_id: number;
    }[];
  } | null;
};

export type TValidateBankAccountPayload = {
  accountNumber?: string;
  bankCode: string;
  bankName?: string;
  countryCode: string;
};

export type TValidateAccountNumberApiResponse = {
  name: string;
};
export type TConnectBankPayload = {
  accountNumber: string;
  bankCode: string;
  bankName: string;
  countryCode: string;
  branchCode?: string;
  routingNumber?: string;
};
export type TUpdateConnectedBankPayload = TConnectBankPayload & {
  otp: string;
};

export type TBank = {
  id: number;
  userId: number;
  country_name: string;
  country_code: string;
  currency: string;
  bank_name: string;
  bank_code: string;
  account_number: string;
  account_name: string;
  status_id: string;
  statusName: string;
  branch_code: string;
  provider: string;
  createdAt: string;
  updatedAt: string;
  loginUrl?: string | null;
};

export type TConnectedBankApiResponse = {
  bank: TBank;
};
export type TConnectBankApiResponse = {
  bank: {
    userId: number;
    country_name: string;
    country_code: string;
    bank_name: string;
    bank_code: string;
    account_number: string;
    account_name: string;
    provider: string;
    id: number;
    createdAt: string;
    updatedAt: string;
  };
};
export type TransactionChargeSettingsValidApiResponse = {
  charge_bearer: ChargeBearer;
  createdAt: string;
  id: number;
  updatedAt: string;
  user_id: number;
};

// Wrapper hooks that use the repository pattern (always v2)
// and add context dispatch for backwards compatibility

const useGetPayoutCountries = () => {
  const {dispatch} = usePayoutSettingsContext();
  const query = useDomainGetPayoutCountries();

  // Dispatch to context when data changes (for backwards compatibility)
  useEffect(() => {
    if (query.isSuccess && query.data?.data?.countries) {
      dispatch({
        type: "update-payout-countries",
        payload: {
          countries: query.data.data.countries,
        },
      });
    }
  }, [query.isSuccess, query.data, dispatch]);

  return query;
};

const useGetPayoutBanksQuery = ({countryCode = null}: TGetPayoutBanksPayload) => {
  const {dispatch} = usePayoutSettingsContext();
  const query = useDomainGetPayoutBanksQuery({countryCode});

  // Dispatch to context when data changes (for backwards compatibility)
  useEffect(() => {
    if (query.isSuccess && query.data?.data) {
      dispatch({
        type: "update-payout-banks",
        payload: {
          banks: query.data.data,
          countryCode,
        },
      });
    }
  }, [query.isSuccess, query.data, countryCode, dispatch]);

  useEffect(() => {
    if (query.isError) {
      dispatch({
        type: "update-payout-banks",
        payload: {
          banks: [],
          countryCode: null,
        },
      });
    }
  }, [query.isError, dispatch]);

  return query;
};

const useGetPayoutBanks = () => {
  return useDomainGetPayoutBanks();
};

const useGetPayoutBankBranchesQuery = ({
  bankId = null,
}: TGetPayoutBankBranchesPayload) => {
  const {dispatch} = usePayoutSettingsContext();
  const query = useDomainGetPayoutBankBranchesQuery({bankId});

  // Dispatch to context when data changes (for backwards compatibility)
  useEffect(() => {
    if (query.isSuccess && query.data?.data) {
      dispatch({
        type: "update-payout-bank-branches",
        payload: {
          branches: query.data.data,
          bankId,
        },
      });
    }
  }, [query.isSuccess, query.data, bankId, dispatch]);

  useEffect(() => {
    if (query.isError) {
      dispatch({
        type: "update-payout-bank-branches",
        payload: {
          branches: null,
          bankId: null,
        },
      });
    }
  }, [query.isError, dispatch]);

  return query;
};

const useGetBankBranches = () => {
  return useDomainGetBankBranches();
};

const useValidateBankAccount = (payload: TValidateBankAccountPayload) => {
  const {dispatch} = usePayoutSettingsContext();
  const query = useDomainValidateBankAccount(payload);

  // Dispatch to context when data changes (for backwards compatibility)
  useEffect(() => {
    if (query.isSuccess && query.data) {
      dispatch({
        type: "update-connect-bank-account-name-value",
        payload: query.data.name,
      });
    }
  }, [query.isSuccess, query.data, dispatch]);

  useEffect(() => {
    if (query.isError) {
      dispatch({
        type: "update-connect-bank-account-name-value",
        payload: null,
      });
    }
  }, [query.isError, dispatch]);

  return query;
};
// useVerifyAccountNumberMutation is now re-exported from ../features/bank-accounts
// useConnectBank is now re-exported from ../features/bank-accounts
// useUpdateConnectedBank is now re-exported from ../features/bank-accounts
// useGetConnectedBankAccount is now re-exported from ../features/bank-accounts
// useConnectToStripe is now re-exported from ../features/bank-accounts

/**
 * @deprecated Use useWithdrawableBalance from "../features/payouts"
 */
const useFetchAccountBalance = useFeatureFetchAccountBalance;

/**
 * @deprecated Use usePayoutHistory from "../features/payouts"
 */
const useFetchPayoutHistory = useFeatureFetchPayoutHistory;

/**
 * @deprecated Use individual hooks from "../features/payouts"
 */
const useWithdrawalDetails = useFeatureWithdrawalDetails;

/**
 * @deprecated Use useTransactionChargeSettings from "../features/transaction-charge-settings"
 */
const useFetchTransactionChargeSettings = useFeatureChargeSettings;

/**
 * @deprecated Use useUpsertTransactionChargeSettings from "../features/transaction-charge-settings"
 */
const useUpdateTransactionChargeSettings = useFeatureUpdateChargeSettings;
// Hooks defined in this file (not re-exported from bank-accounts feature)
export {
  // Bank account hooks (kept here for backwards compatibility with untyped axios responses)
  useGetPayoutCountries,
  useGetPayoutBanks,
  useGetBankBranches,
  useValidateBankAccount,
  useGetPayoutBanksQuery,
  useGetPayoutBankBranchesQuery,
  // Balance & Withdrawal
  useFetchAccountBalance,
  useWithdrawalDetails,
  useFetchPayoutHistory,
  // Transaction settings
  useFetchTransactionChargeSettings,
  useUpdateTransactionChargeSettings,
};
